import { AlertCircle, CheckCircle2, Info, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

type ToastVariant = "success" | "error" | "info";

type Toast = {
  id: string;
  message: string;
  variant: ToastVariant;
};

type ToastContextValue = {
  showToast: (message: string, variant?: ToastVariant) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION_MS = 5000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback<ToastContextValue["showToast"]>((message, variant = "info") => {
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;
    setToasts((current) => [...current, { id, message, variant }]);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: (id: string) => void }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(toast.id), TOAST_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [toast.id, onDismiss]);

  const variants: Record<ToastVariant, { icon: typeof CheckCircle2; classes: string; iconClasses: string }> = {
    success: {
      icon: CheckCircle2,
      classes: "border-[rgba(4,120,87,0.2)] bg-[#ecfdf5] dark:bg-[rgba(16,185,129,0.14)] text-[#065f46]",
      iconClasses: "text-[#047857]",
    },
    error: {
      icon: AlertCircle,
      classes: "border-[rgba(220,38,38,0.2)] bg-[#fef2f2] dark:bg-[rgba(239,68,68,0.14)] text-[#7f1d1d]",
      iconClasses: "text-[#b91c1c]",
    },
    info: {
      icon: Info,
      classes: "border-line bg-surface text-foreground",
      iconClasses: "text-[#1d4ed8]",
    },
  };

  const config = variants[toast.variant];
  const Icon = config.icon;

  return (
    <div
      role={toast.variant === "error" ? "alert" : "status"}
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border px-4 py-3 shadow-[0_12px_32px_-12px_rgba(11,18,32,0.25)] ${config.classes}`}
    >
      <Icon size={18} className={`mt-0.5 shrink-0 ${config.iconClasses}`} />
      <div className="flex-1 text-sm font-medium leading-5">{toast.message}</div>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className="-m-1 shrink-0 rounded-md p-1 opacity-60 transition hover:bg-black/5 hover:opacity-100"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
}
